import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTemplate} from '../../../../context/TemplateContext';
import {loadFontToElement} from "../../../../utils/fontsLoader";
import {toast} from 'react-toastify'; // Import toast
import {useTranslation} from 'react-i18next'; // Import useTranslation
import Asset, {isVideoAsset} from '../../../../models/Asset';
import {
    createJob,
    fetchGeneratedImageBlob,
    pollJobUntilComplete,
    GenerationActionSource,
    HtmlEngineJobRequest,
    JobStatus
} from '../../../../services/jobService';
import {selectSelectedWorkspaceId} from '../../../../utils/userSlice';
import {designKeyOf, mergeUserLayers} from '../../../../scene/userLayers';
import {visibleScene} from '../../../../scene/rowVisibility';
import {Scene, SceneElement} from '../../../../scene/types';
import logger from '../../../../utils/logger';
// Konva / renderScene / registerSceneFonts are no longer needed here — scene
// templates now render server-side via the generator (Konva-on-Node). The
// editor preview still uses them via SceneRenderer.tsx.

// Progress stage thresholds
enum ProgressStage {
    INITIALIZING = 0,
    LAUNCHING_BROWSER = 10,
    RENDERING_HTML = 15,
    DOWNLOADING_SOURCE = 30,  // Downloading source video
    ENCODING_VIDEO = 45,
    UPLOADING = 90,
    FINALIZING = 95,
    FETCHING_RESULT = 98,     // Fetching generated video
    COMPLETE = 100
}

// Polling budgets. Stills render in a couple of seconds — they only go through
// the job system so every generation is recorded server-side — so they get a
// much shorter deadline than a video composite.
const IMAGE_POLL_TIMEOUT_MS = 120000;   // 2 minutes
const VIDEO_POLL_TIMEOUT_MS = 600000;   // 10 minutes

export interface ProcessImageOptions {
    /** Why this generation ran (contract §5). Defaults to an explicit user action. */
    actionSource?: GenerationActionSource;
    /**
     * Called with the server jobId the moment the job exists. The jobId IS the
     * generationId of the durable record, so callers use it as the correlation
     * id for their stats events instead of a client-side random id.
     */
    onJobCreated?: (jobId: string) => void;
}

/** The element whose video an export composites, on the VISIBLE scene: a video
 *  in a hidden row or a switched-off layer must not make the export an MP4. */
export const videoElementOf = (scene: Scene, selections: Record<string, unknown>): SceneElement | undefined =>
    scene.elements.find((e) => {
        const value = e.type === 'image' && e.inputKey ? selections[e.inputKey] : undefined;
        return !!value && typeof value === 'object' && isVideoAsset(value as Asset);
    });

export const useImageProcessor = (initialDesignIndex: number) => {
    const [isLoading, setIsLoading] = useState(false);
    // Aborts any in-flight job polling when the consumer (e.g. ShareModal)
    // unmounts — otherwise a video job keeps polling for up to 10 minutes
    // after the user navigated away.
    const abortRef = useRef<AbortController>();
    if (!abortRef.current) abortRef.current = new AbortController();
    useEffect(() => () => abortRef.current?.abort(), []);
    const [lastBlob, setLastBlob] = useState<Blob | null>(null);
    // Videos are streamed straight from this presigned S3 URL (<video src>)
    // instead of being buffered into a Blob — 20-45MB downloads routinely
    // fail on mobile connections. The Blob is fetched lazily on download/share.
    const [lastVideoUrl, setLastVideoUrl] = useState<string | null>(null);
    const [lastMediaType, setLastMediaType] = useState<'image' | 'video'>('image');
    const [progress, setProgress] = useState(0);
    const {template, selections, imageTransforms, sceneTransforms, imageFit, userLayersByDesign, userLayersEnabled, visibility, setSelectedTransformedImage} = useTemplate();
    const {t} = useTranslation();
    // Stamped on every job so the server-side generation record can attribute
    // the render to a workspace (the API still verifies it against the JWT).
    const workspaceId = useSelector(selectSelectedWorkspaceId);

    // Get localized progress message based on progress percentage
    const getProgressMessage = useCallback((progressValue: number): string => {
        if (progressValue >= ProgressStage.COMPLETE) {
            return t("editor.progress.complete");
        } else if (progressValue >= ProgressStage.FETCHING_RESULT) {
            return t("editor.progress.fetchingResult");
        } else if (progressValue >= ProgressStage.FINALIZING) {
            return t("editor.progress.finalizing");
        } else if (progressValue >= ProgressStage.UPLOADING) {
            return t("editor.progress.uploading");
        } else if (progressValue >= ProgressStage.ENCODING_VIDEO) {
            return t("editor.progress.encoding");
        } else if (progressValue >= ProgressStage.DOWNLOADING_SOURCE) {
            return t("editor.progress.downloadingSource");
        } else if (progressValue >= ProgressStage.RENDERING_HTML) {
            return t("editor.progress.rendering");
        } else if (progressValue >= ProgressStage.LAUNCHING_BROWSER) {
            return t("editor.progress.launchingBrowser");
        } else {
            return t("editor.progress.initializing");
        }
    }, [t]);

    // Derived progress message from progress value
    const progressMessage = getProgressMessage(progress);

    /** The scene the editor previewed (useMergedScene's pipeline), for any
     *  design index — a hook can't be called inside processImage. */
    const exportScene = useCallback((designIndex: number) => {
        const design = template?.designs?.[designIndex];
        if (!design?.scene) return undefined;
        const merged = mergeUserLayers(
            design.scene,
            userLayersByDesign[designKeyOf(design, designIndex)],
            selections as any,
            userLayersEnabled,
        ) ?? design.scene;
        return visibleScene(merged, template?.toggles, visibility);
    }, [template, userLayersByDesign, selections, userLayersEnabled, visibility]);

    // A scene design asks its VISIBLE scene; legacy HTML has no rows.
    const hasVideoBackground = useCallback((designIndex: number = initialDesignIndex) => {
        const scene = exportScene(designIndex);
        if (scene) return !!videoElementOf(scene, selections);
        return Object.entries(selections).some(([key, selection]) => {
            if (template?.inputs?.[key]?.type === "image" && selection) {
                return isVideoAsset(selection as Asset);
            }
            return false;
        });
    }, [exportScene, initialDesignIndex, selections, template]);

    // NEW: Get video URL from selections
    const getVideoUrl = useCallback(() => {
        for (const [key, selection] of Object.entries(selections)) {
            if (template?.inputs?.[key]?.type === "image" && selection) {
                const asset = selection as Asset;
                if (isVideoAsset(asset)) {
                    return asset.highRes;
                }
            }
        }
        return null;
    }, [selections, template]);

    // NEW: Get video element positioning information
    const getVideoPosition = useCallback((div: HTMLDivElement) => {
        for (const [key, selection] of Object.entries(selections)) {
            if (template?.inputs?.[key]?.type === "image" && selection) {
                const asset = selection as Asset;
                if (isVideoAsset(asset)) {
                    // Find the image element that's being replaced by the video
                    const imgElement = div.querySelector(`[data-name="${key}"]`) as HTMLElement;
                    if (imgElement) {
                        // Get the container that has the positioning
                        const container = imgElement.closest('.image-container, .frame') as HTMLElement;
                        if (container) {
                            const containerStyle = container.style;
                            return {
                                left: parseFloat(containerStyle.left) || 0,
                                top: parseFloat(containerStyle.top) || 0,
                                width: parseFloat(containerStyle.width) || parseFloat(imgElement.style.width) || 0,
                                height: parseFloat(containerStyle.height) || parseFloat(imgElement.style.height) || 0
                            };
                        }
                    }
                    // Fallback: try to get from image element itself
                    if (imgElement) {
                        return {
                            left: parseFloat(imgElement.style.left) || 0,
                            top: parseFloat(imgElement.style.top) || 0,
                            width: parseFloat(imgElement.style.width) || 0,
                            height: parseFloat(imgElement.style.height) || 0
                        };
                    }
                }
            }
        }
        return null;
    }, [selections, template]);

    // NEW: Get video duration from selections
    const getVideoDuration = useCallback(() => {
        for (const [key, selection] of Object.entries(selections)) {
            if (template?.inputs?.[key]?.type === "image" && selection) {
                const asset = selection as Asset;
                if (isVideoAsset(asset)) {
                    if (asset.duration) {
                        return asset.duration;
                    } else {
                        console.warn('Video asset found but duration is missing/falsy:', asset.duration);
                    }
                }
            }
        }
        console.warn('No video duration found, using default 5 seconds');
        return 5; // Default duration
    }, [selections, template]);

    // `renderVideoAsThumbnail` = we're generating a still PNG from a selection
    // that contains a video: paint the video's thumbnail image into the element
    // instead of hole-punching it transparent (which is only correct when the
    // Lambda composites the real video underneath).
    const applySelections = useCallback((div: HTMLDivElement, renderVideoAsThumbnail = false) => {
        logger.debug("selections", selections, template?.inputs)

        Object.entries(selections).forEach(([key, selection]) => {
            const textElements = div.querySelectorAll(`[data-name="${key}"]`);
            const imgElements = div.querySelectorAll(`[data-name="${key}"]`) as NodeListOf<HTMLImageElement>;

            if (template?.inputs?.[key]?.type === "text") {
                logger.debug("input text", key, template?.inputs?.[key], selection, textElements)
                textElements.forEach(element => {
                    element.textContent = selection as string;
                });
            }

            if (template?.inputs?.[key]?.type === "image") {
                // console.log("input image", key, template?.inputs?.[key], selection, imgElements)
                const asset = selection as Asset;
                const isVideo = isVideoAsset(asset);

                imgElements.forEach(element => {
                    if (isVideo && renderVideoAsThumbnail) {
                        // Still-image output from a video selection: draw the
                        // video's thumbnail so the PNG isn't blank.
                        element.src = asset.thumbnail || asset.highRes;
                        logger.debug(`[VIDEO] Set thumbnail for video element (image output): ${key}`);
                    } else if (isVideo) {
                        // For videos, use a transparent placeholder instead of the video URL
                        // The actual video will be rendered by the lambda as a background layer
                        // Set to transparent 1x1 pixel data URI so the element exists but is invisible
                        element.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                        logger.debug(`[VIDEO] Set transparent placeholder for video element: ${key}`);

                        // Make ALL parent containers transparent up the chain
                        let parent = element.parentElement;
                        while (parent && parent !== div) {
                            const bgColor = parent.style.background || parent.style.backgroundColor;
                            if (bgColor) {
                                parent.style.background = 'transparent';
                                logger.debug(`[VIDEO] Made parent transparent: ${parent.className || parent.tagName} (was: ${bgColor})`);
                            }
                            parent = parent.parentElement;
                        }
                    } else {
                        element.src = asset.highRes;
                    }

                    if (imageTransforms?.[key]) {
                        logger.debug("imageTransforms?.[key]", imageTransforms?.[key])
                        const {width, height, top, left} = imageTransforms?.[key]
                        element.style.position = 'relative';
                        element.style.width = `${width}px`;
                        element.style.height = `${height}px`;
                        element.style.left = `${left}px`;
                        element.style.top = `${top}px`;
                    }
                });
            }
        });
    }, [selections, template, imageTransforms]);

    const processImage = useCallback(async (designIndex = initialDesignIndex, outputFormat?: 'image' | 'video', successCallback?: (durationMs: number) => void, errorCallback?: (error: any) => void, options?: ProcessImageOptions) => {
        // Guard the `template!` accesses below — generate can race a template
        // reset/unload (navigation, resetTemplateContext); bail, don't throw.
        if (!template) return;
        // disable selections:
        setSelectedTransformedImage(undefined)

        setIsLoading(true);
        setProgress(0);
        setLastBlob(null);
        setLastVideoUrl(null);

        // Resolve the desired output. Default (no explicit format) preserves the
        // legacy behavior: produce a video whenever a video is selected. An
        // explicit 'image' forces a still PNG even when a video is selected;
        // 'video' only makes sense when a video is actually selected.
        const videoSelected = hasVideoBackground(designIndex);
        const wantVideo = (outputFormat ? outputFormat === 'video' : videoSelected) && videoSelected;

        const design = template!.designs[designIndex];
        const scene = exportScene(designIndex);

        // Provenance for the durable generation record (contract §5). References
        // only — the API re-verifies template access and snapshots the names.
        const provenance = {
            templateId: template!.id,
            designKey: design?.key ?? String(designIndex),
            actionSource: options?.actionSource ?? 'explicit' as const,
            ...(workspaceId ? {workspaceId} : {}),
        };

        // Canvas ("scene") engine: send the scene IR + values to the generator
        // (server-side Konva-on-Node) — same render path the editor uses, but
        // executed in the Lambda so high-res assets stay off the client. The
        // editor itself still uses client-side Konva for the live preview.
        if (scene) {
            // Locate the video-bound element so the server can hole-punch
            // it (and use videoPosition for the FFmpeg overlay step).
            const videoEl = wantVideo ? videoElementOf(scene, selections) : undefined;
            const video = videoEl ? selections[videoEl.inputKey!] as Asset : undefined;
            setLastMediaType(wantVideo ? 'video' : 'image');
            const startTime = performance.now();
            try {
                let skipElementId: string | undefined;
                let videoPosition: { left: number; top: number; width: number; height: number } | null = null;
                if (videoEl) {
                    skipElementId = videoEl.id;
                    const tx = sceneTransforms[videoEl.id] || {};
                    videoPosition = {
                        left: tx.x ?? videoEl.x, top: tx.y ?? videoEl.y,
                        width: tx.w ?? videoEl.w, height: tx.h ?? videoEl.h,
                    };
                }

                // Server-side render — the API/Lambda contract is a discriminated
                // union on `engine`. `selections` carries the per-input values
                // (Asset object for image inputs, string for text), which is
                // exactly the shape Konva-on-Node expects in `values`.
                const scenePayload = {
                    engine: 'scene' as const,
                    scene,
                    values: selections as Record<string, unknown>,
                    transforms: sceneTransforms as Record<string, unknown>,
                    // Which image fields the user replaced -> the server-side
                    // renderer contains them instead of cover-cropping, exactly
                    // as the preview does.
                    imageFit: imageFit as Record<string, string>,
                    fonts: template!.fonts as Record<string, unknown>,
                    width: design.width,
                    height: design.height,
                };

                if (video) {
                    // Video path: async job — API forwards the scene payload to
                    // the Lambda; Lambda renders the overlay (Konva-on-Node)
                    // then composites onto the video with FFmpeg.
                    const { jobId } = await createJob({
                        ...scenePayload,
                        ...provenance,
                        hasVideo: true,
                        videoUrl: video.highRes,
                        // Same 5s fallback as getVideoDuration, for THIS video.
                        videoDuration: video.duration || 5,
                        outputFormat: 'mp4',
                        skipElementId,
                        skipBackground: true,
                        ...(videoPosition ? { videoPosition } : {}),
                    });
                    options?.onJobCreated?.(jobId);
                    const finalStatus = await pollJobUntilComplete(jobId, (s: JobStatus) => setProgress(s.progress), VIDEO_POLL_TIMEOUT_MS, undefined, abortRef.current?.signal);
                    if (!finalStatus.videoUrl) throw new Error('No video URL in response');
                    setLastVideoUrl(finalStatus.videoUrl);
                } else {
                    // Still PNG path: same async job flow as video, so the
                    // render is recorded server-side. The completed status
                    // carries a fresh presigned imageUrl we pull into a Blob.
                    const { jobId } = await createJob({
                        ...scenePayload,
                        ...provenance,
                        outputFormat: 'png',
                    });
                    options?.onJobCreated?.(jobId);
                    const finalStatus = await pollJobUntilComplete(jobId, (s: JobStatus) => setProgress(s.progress), IMAGE_POLL_TIMEOUT_MS, undefined, abortRef.current?.signal);
                    setLastBlob(await fetchGeneratedImageBlob(finalStatus, abortRef.current?.signal));
                }
                setProgress(ProgressStage.COMPLETE);
                successCallback?.(performance.now() - startTime);
            } catch (error) {
                // Unmount abort (user navigated away) — not an error, stay silent.
                if ((error as Error)?.name === 'AbortError') return;
                console.error('Error rendering scene:', error);
                toast.error(t('error.imageProcessing') || 'Failed to generate media.');
                errorCallback?.(String(error ?? ''));
            } finally {
                setIsLoading(false);
            }
            return;
        }

        const div = document.createElement('div');
        div.innerHTML = design.html || '';
        // Still-image output from a video selection: render the video's thumbnail
        // in place instead of a transparent hole (no Lambda video composite).
        applySelections(div, videoSelected && !wantVideo);

        // load fonts
        for (let [name, font] of Object.entries(template!.fonts)) {
            if (font?.urls) {
                loadFontToElement(name, font.urls!, div)
            }
        }

        // NEW: Detect video and prepare request
        const hasVideo = wantVideo;
        const videoUrl = hasVideo ? getVideoUrl() : null;
        const videoDuration = hasVideo ? getVideoDuration() : 5;
        const videoPosition = hasVideo ? getVideoPosition(div) : null;

        // Legacy HTML template — `engine: 'html'` selects the Puppeteer path
        // in the discriminated-union job schema. Typed as HtmlEngineJobRequest
        // so the build catches missing/wrong fields at call sites.
        const postData: HtmlEngineJobRequest = {
            engine: 'html',
            html: div.outerHTML,
            width: design.width,
            height: design.height,
            ...provenance,
        };

        // NEW: Add video parameters if detected
        if (hasVideo && videoUrl) {
            postData.hasVideo = true;
            postData.videoUrl = videoUrl;
            postData.videoDuration = videoDuration;
            postData.outputFormat = 'mp4';

            // Add video positioning if available
            if (videoPosition) {
                postData.videoPosition = videoPosition;
                logger.debug('[VIDEO] Video position:', videoPosition);
            }

            setLastMediaType('video');
        } else {
            setLastMediaType('image');
        }

        try {
            const startTime = performance.now();

            // Both outputs go through the async job system with polling — the
            // only difference is the deadline and how the result is consumed.
            if (hasVideo && videoUrl) {
                // Create job
                const { jobId } = await createJob(postData);
                logger.debug('[VIDEO] Job created:', jobId);
                options?.onJobCreated?.(jobId);

                // Poll for completion with progress updates
                const finalStatus = await pollJobUntilComplete(
                    jobId,
                    (status: JobStatus) => {
                        setProgress(status.progress);
                    },
                    VIDEO_POLL_TIMEOUT_MS,
                    undefined,
                    abortRef.current?.signal
                );

                if (finalStatus.videoUrl) {
                    setLastVideoUrl(finalStatus.videoUrl);
                } else {
                    throw new Error('No video URL in response');
                }
            } else {
                // Stills: shorter deadline, and the completed status carries a
                // fresh presigned imageUrl which we pull into a Blob for the
                // existing preview/download/share UX.
                const { jobId } = await createJob({...postData, outputFormat: 'png'});
                logger.debug('[IMAGE] Job created:', jobId);
                options?.onJobCreated?.(jobId);

                const finalStatus = await pollJobUntilComplete(
                    jobId,
                    (status: JobStatus) => {
                        setProgress(status.progress);
                    },
                    IMAGE_POLL_TIMEOUT_MS,
                    undefined,
                    abortRef.current?.signal
                );

                setLastBlob(await fetchGeneratedImageBlob(finalStatus, abortRef.current?.signal));
            }

            const endTime = performance.now();
            const durationMs = endTime - startTime;
            setProgress(ProgressStage.COMPLETE);
            successCallback?.(durationMs);
        } catch (error) {
            // Unmount abort (user navigated away) — not an error, stay silent.
            if ((error as Error)?.name === 'AbortError') return;
            console.error('Error processing media:', error);
            toast.error(t("error.imageProcessing") || "Failed to generate media.");
            errorCallback?.(String(error ?? ""))
        } finally {
            setIsLoading(false);
        }
    }, [template, initialDesignIndex, applySelections, setSelectedTransformedImage, selections, imageTransforms, sceneTransforms, imageFit, exportScene, workspaceId, t, hasVideoBackground, getVideoUrl, getVideoDuration, getVideoPosition]); // sceneTransforms/imageFit/exportScene MUST stay here — without them the generate payload ships stale position/fontSize overrides, a stale cover/contain decision, or a scene missing the user's own layers or still showing a row they hid

    return {isLoading, processImage, hasVideoBackground, lastBlob, setLastBlob, lastVideoUrl, lastMediaType, progress, progressMessage};
};
